/**
 * Every spawn code in a pack, and how we know each one.
 *
 * A server owner downloads a vehicle pack and needs one thing out of it before
 * anything else: the list of names `/car <name>` will actually spawn. The pack
 * rarely ships that list, and when it does the list is often what the author
 * meant rather than what the files say. So it has to be read out of the files,
 * and there are exactly two places to read it from — `vehicles.meta`, which is
 * where the game reads it from, and the names of the `.yft` models in `stream/`,
 * which is a guess.
 *
 * Both tiers are already written. `parseVehiclesMeta` is the authoritative one
 * and `spawnCandidates` is the guess, and `spawnCandidates` already carries the
 * argument for why it deletes nothing. This module is the part above them: it
 * decides which tier a pack gets, cross-checks the names against the rest of the
 * pack, badges the three things that break a live server, and writes the four
 * files a user copies out.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SIX THINGS IT DOES THAT THE TOOL IT REPLACES DOES NOT
 *
 * 1. It trusts only a *registered* meta. FiveM reads a `.meta` because the
 *    manifest's `data_file` block names it, not because it is on disk. A
 *    `vehicles.meta` nobody registered defines nothing at runtime, and reporting
 *    its rows as live spawn codes hands the user a list of names that produce
 *    "model not found" and no explanation. The codes are still listed — they are
 *    what the author meant, and the repair is one manifest line — but the row
 *    says `unregistered` and the pack carries `manifest.meta.not_registered`
 *    with the edit that fixes it.
 *
 * 2. It cross-checks every name. A code with no `.yft`, no handling entry or no
 *    carvariations row is the difference between a pack that works and a pack
 *    that looks like it works, and "3 codes in vehicles.meta have no model file"
 *    is the sentence somebody evaluating a paid pack is actually trying to buy.
 *    `analyzeVehicleMeta` computes all three; nothing here re-derives them.
 *
 * 3. It surfaces the parse error. The prior art computes the XML parser's
 *    message, truncates it to 200 characters, returns it and never reads it,
 *    which turns the single most confusing failure a user of this tool can hit —
 *    "why is my pack showing guesses when it has a vehicles.meta?" — into
 *    silence. Here the message is the answer to that question and it is printed.
 *
 * 4. It never drops a pack. A pack that produced nothing is the one the user is
 *    most likely to be asking about, and dropping it from a report whose whole
 *    job is "tell me what is in this folder" is a worse answer than a zero. Every
 *    resource handed in comes back, with a sentence saying why its count is what
 *    it is.
 *
 * 5. It sorts and dedupes case-insensitively. The engine hashes a model name
 *    case-insensitively, so `ALPHA_GT` and `alpha_gt` are one name and neither
 *    sorts above `bmwm3f80`. A bare `.sort()` puts every upper-cased name in a
 *    block at the top, which reads as a grouping the data does not have.
 *
 * 6. It shows what it did not list, with the undo. Everything this tool saw and
 *    chose not to call a spawn code is in `filtered`, with the sentence that
 *    demoted it, and `restoreFiltered` puts any of it back. The blacklist that
 *    silently ate `gtr_model`, `police_extra` and `sultan_roof` cannot recur in a
 *    version where nothing is hidden and the demotion is a row the user can undo.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT IS NOT, AND WHERE THAT SHOWS
 *
 * This is a spawn-code reader, not a resource audit. It runs the vehicle
 * analysis in full — that is the only honest way to cross-check a name — and then
 * keeps the findings that are about spawn codes, because a list of mod kit id
 * collisions under a list of spawn codes teaches the reader to scroll past both.
 * The filter is on `code`, which is a permanent identifier, never on wording.
 * `SPAWN_INDEX_SCOPE_NOTE` says so in every export, so nobody reads a short
 * findings list as a clean bill of health.
 *
 * Two costs are taken deliberately rather than hidden:
 *
 * - A `vehicles.meta` is parsed twice, once by `analyzeVehicleMeta` for the
 *   cross-checks and once here. `VehicleEntity` does not carry
 *   `vehicleMakeName`, and the make is half of the one export that makes this
 *   tool worth using. Re-reading one small file per pack is cheaper than a
 *   second copy of the cross-check rules, and far cheaper than an export that
 *   drops the field.
 *
 * - The registration check is computed here from `entriesOfKind`,
 *   `manifestPatternToRegExp` and `pathKey` rather than taken from
 *   `analyzeManifest`. `analyzeManifest` answers "is any meta in this folder
 *   unregistered", and the question here is "is *this* `vehicles.meta`
 *   registered", which has to be answered per file because it decides what a
 *   row says about itself. The finding it raises is the same shared code with
 *   the same `fix.label` shape, so `attachManifestFixes` produces the same edit
 *   — qualified by the resource it belongs to, which is the next paragraph.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY PATH THIS MODULE EMITS IS QUALIFIED BY ITS RESOURCE
 *
 * `vehicles.meta` and `fxmanifest.lua` name a different file in every pack in a
 * drop, and three separate mechanisms key on that bare path: `findingKey`
 * dedupes on it, `groupByEntity` groups on it, and a surface that applies a
 * one-click fix reads the file back by it. Left unqualified, all three merge
 * across the identity boundary intake exists to preserve — ten packs with three
 * broken metas report one broken meta, and a repair written for one pack is
 * appended to another pack's manifest and reported as a success. So findings,
 * entity ids and fix edits are all spelled `${identity}/${path}`, the way
 * `SpawnCode.source` always was.
 *
 * Nothing here reads a byte the caller did not hand it, and nothing leaves the
 * tab. What the caller did *not* hand it is its own answer: a `vehicles.meta`
 * that is in the file inventory and absent from the texts was not opened, and
 * saying "this pack has no vehicles.meta" about it would be this module
 * asserting the opposite of the file list in front of it.
 */

import { MANIFEST_FILE_NAMES } from "./analyze";
import { csvCell } from "./csv";
import { dataFileKindFor } from "./data/dataFileKinds.v1";
import { isEmergencyVehicleName } from "./data/emergencyVehicles.v1";
import { VANILLA_VEHICLE_MODELS } from "./data/vanillaVehicles.v1";
import { dedupeFindings, sortFindings } from "./findings";
import { attachManifestFixes } from "./fixSuggestions";
import { disambiguateLabels, EM_DASH_LABEL_STYLE, PROSE_LABEL_STYLE } from "./labels";
import {
  entriesOfKind,
  isGlobPattern,
  manifestPatternToRegExp,
  parseManifest,
} from "./manifest";
import { baseName, extensionOf, isInStreamFolder, normalizeName, pathKey } from "./normalize";
import { collectStreamAssets, spawnCandidates } from "./streamAssets";
import type {
  GtaEntityKind,
  GtaFinding,
  GtaFindingCode,
  GtaFindingTag,
  GtaResourceInput,
  ParsedManifest,
  VanillaAssetNameIndex,
} from "./types";
import {
  analyzeVehicleMeta,
  metaKindOf,
  parseMetaXml,
  parseTextEntries,
  parseVehiclesMeta,
  textEntryHash,
} from "./vehicleMeta";

// ─── Identity ────────────────────────────────────────────────────────────────

/** Who made an export, so one found in a Discord channel months later can be placed. */
export const SPAWN_INDEX_TOOL = "The Workshop — Spawn Index";

/**
 * The caveat every export carries, once, plainly.
 *
 * Two claims are being limited at the same time. The list is what these files
 * say, not what a running server did — nothing here has started one. And the
 * list is *spawn codes*: a pack can be full of problems this tool never looked
 * at, so a short findings list here is not a clean pack.
 */
export const SPAWN_INDEX_SCOPE_NOTE =
  "This is a static read of the files as they were supplied: it lists the spawn codes these " +
  "files declare, and it is not proof that the vehicles work in game. It is also not a full " +
  "check of the pack — only the problems that affect a spawn code are reported here.";

// ─── Caps ────────────────────────────────────────────────────────────────────

/**
 * Resources folded into one index.
 *
 * A `resources/` folder of this size is a large server and well inside intake's
 * ceiling, so tripping this is unusual and is reported in `skipped` rather than
 * quietly truncating a list whose whole purpose is to be complete.
 */
const MAX_PACKS = 2_000;

/** Codes listed for one pack. A pack past this is a DLC dump, not a vehicle pack. */
const MAX_CODES_PER_PACK = 2_000;

/** Names carried in `filtered` per pack, which is a diagnostic panel and not a list to ship. */
const MAX_FILTERED_PER_PACK = 500;

/** Lua files scanned for `AddTextEntry`, so a script-heavy resource cannot stall the tab. */
const MAX_LABEL_FILES = 40;

/** Registered labels held for one pack. */
const MAX_LABELS = 5_000;

/**
 * The size past which a `.meta` is not parsed at all.
 *
 * Duplicated from `vehicleMeta.ts`, which is the source of truth and does not
 * export it. The only thing this copy decides is the *wording* of a failure —
 * "too large to parse" instead of an XML parser's message — so if the two ever
 * drift the cost is a less specific sentence, never a wrong answer.
 */
const META_PARSE_CEILING_BYTES = 32 * 1024 * 1024;

/** How much of a parser's complaint is quoted before it is cut, and said to be cut. */
const MAX_PARSE_ERROR_CHARS = 400;

/**
 * The archive a vehicle pack hides inside and nothing here can open.
 *
 * `intake.ts` knows the same fact and does not export it, and the duplication is
 * one string rather than a rule: `.rar` and `.7z` in a `resources/` folder are a
 * download somebody forgot to extract, while `dlc.rpf` *is* the shipped form of
 * a whole DLC pack. The README names `.rpf` as a non-goal and says the tools
 * should say so rather than reporting zero, which is what this constant is for.
 */
const SEALED_ARCHIVE_EXTENSION = "rpf";

// ─── The result ──────────────────────────────────────────────────────────────

/** Where a pack's codes came from. `none` is a real answer and is never hidden. */
export type SpawnTier = "meta" | "guess" | "none";

/**
 * The facts about a code that change what a server owner does next.
 *
 * Badges rather than findings because they belong to the *row*: a duplicate is
 * only visible from above the pack, and a reader scanning fifty codes needs the
 * three that will break something to be visible without opening anything.
 */
export type SpawnBadge =
  /** Another pack in this scan ships the same code. One of the two wins, silently. */
  | "duplicate"
  /** The base game already owns this spawn name, so this replaces it everywhere. */
  | "vanilla"
  /** Emergency fleet, by name. The vehicle every unit on the server drives. */
  | "emergency"
  /** No `.yft` of this name streams here, so spawning it produces nothing. */
  | "no-model"
  /**
   * Nothing in this pack binds this code to a handling entry.
   *
   * Two causes, one badge, and the wording covers both on purpose: the row
   * names no `handlingId` at all, or it names one no `handling.meta` here
   * defines. `analyzeVehicleMeta` raises `vehicle.handling.missing` for both,
   * and a legend that described only the second contradicted the pack's own
   * "No handling.meta was loaded" caveat two lines above it — the same screen
   * saying two things about one pack.
   */
  | "no-handling"
  /** The pack ships a `carvariations.meta` and this code is not in it. */
  | "no-variation"
  /** The meta it was read from is on disk and the manifest never registers it. */
  | "unregistered";

export type SpawnCode = {
  /** Lower-cased: the form the engine hashes, and the key everything compares on. */
  name: string;
  /** As the file writes it, for display. */
  displayed: string;
  tier: "meta" | "guess";
  /** `meta` codes are always `high`; a guess carries the rank `spawnCandidates` gave it. */
  confidence: "high" | "medium" | "low";
  /** How we know, in a sentence the user can argue with. */
  reason: string;
  /** The file the claim came from, resource-qualified, so the reader can open it. */
  source: string;
  /** The resource's own name, allowed to repeat — two packs called `police` is the point. */
  resource: string;
  /** The spelling to print, widened only if another resource here reads the same. */
  resourceLabel: string;
  /** What separates this resource from the one beside it. `GtaResourceInput.identity`. */
  identity: string;
  origin: string | null;
  /** `<gameName>` as written: a text-label key, not a display name. */
  gameName: string | null;
  /** What `AddTextEntry` in this pack registers that key as, when it registers one. */
  displayName: string | null;
  /** `<vehicleMakeName>` as written, also a label key. */
  make: string | null;
  /** What this pack registers the make key as, when it does. */
  makeName: string | null;
  /**
   * Whether every Lua file in this pack was read looking for `AddTextEntry`.
   *
   * It exists because it changes what a `null` `displayName` is allowed to
   * claim. "This pack never registers that label" is a statement about files
   * that were read; after the label scan hits its cap some were not, and the
   * exports have to say the weaker, true thing instead.
   */
  labelScanComplete: boolean;
  handlingId: string | null;
  txdName: string | null;
  audioNameHash: string | null;
  layout: string | null;
  flags: readonly string[];
  badges: readonly SpawnBadge[];
};

export type SpawnPack = {
  name: string;
  /** The heading spelling, from the shared rule in `labels.ts`. */
  label: string;
  identity: string;
  origin: string | null;
  tier: SpawnTier;
  /**
   * Why this tier, and — when the count is zero — why it is zero. Never empty.
   *
   * `noteHeadline` + `noteCaveats` joined by a space. Kept as one string
   * because every export prints it as one line and none of them has a header
   * to compare it against.
   */
  note: string;
  /**
   * The first sentence of `note`: the tier, the count and where it was read.
   *
   * Split out because on a `meta`-tier pack it is the third statement of the
   * same fact on one screen — the surface already draws the tier as a chip and
   * the count as a number in the pack's heading, and then this repeats both in
   * a sentence two lines below them. A 40-resource folder prints that sentence
   * forty times. A surface with a heading of its own drops this and keeps
   * `noteCaveats`; a surface without one (every export) prints `note`.
   */
  noteHeadline: string;
  /**
   * Everything in `note` that is not the headline: unread files, unregistered
   * metas, a guess that could be incomplete, a name restored by hand.
   *
   * Every entry is a limit on what the list in front of the reader means, so
   * none of them is ever dropped by a surface — that is the whole reason the
   * split is headline-versus-caveats rather than first-sentence-versus-rest.
   */
  noteCaveats: readonly string[];
  codes: SpawnCode[];
  /**
   * Names seen and not listed, each carrying the sentence that demoted it.
   *
   * A `SpawnCode` rather than a lesser shape on purpose: restoring one is then
   * moving a row, not rebuilding it, so what the user gets back after an undo is
   * exactly what they would have got had the ranking agreed with them.
   */
  filtered: SpawnCode[];
  findings: GtaFinding[];
  skipped: string[];
};

export type SpawnIndex = {
  packs: SpawnPack[];
  /** Every listed code across every pack, sorted case-insensitively by name. */
  codes: SpawnCode[];
  /** Findings only a view across the packs can produce: the duplicates. */
  findings: GtaFinding[];
  skipped: string[];
  counts: {
    packs: number;
    codes: number;
    fromMeta: number;
    guessed: number;
    filtered: number;
    /** Packs that produced no code at all. Listed, always, with a reason. */
    empty: number;
    /** Codes a second pack also ships. */
    duplicated: number;
  };
};

export type SpawnIndexOptions = {
  /**
   * The stock asset name index, when the caller loaded it.
   *
   * Absent is normal and is not a failure: the 900-odd stock *vehicle* spawn
   * names are compiled in and always checked, so the `vanilla` badge works
   * without it. What the index adds is every other streamed asset name, and its
   * absence is recorded in `skipped` rather than read as a pass.
   */
  vanillaNames?: VanillaAssetNameIndex;
};

// ─── Ordering ────────────────────────────────────────────────────────────────

/**
 * Ordering that does not depend on the machine.
 *
 * `localeCompare` reads the host's ICU data, so the same pack scanned in a
 * browser and in the desktop app can produce two different orderings of one
 * list and a saved export starts showing a diff nobody made. Same argument, and
 * same answer, as `streamAssets.ts`.
 */
function compareText(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * Spawn names compared the way the engine hashes them: case-folded.
 *
 * The raw comparison is the tie-break rather than the rule, so two spellings of
 * one name still order deterministically without `ALPHA_GT` jumping above
 * `bmwm3f80` — which is what a bare `.sort()` does, and it reads as a grouping
 * that is not in the data.
 */
function compareNames(a: string, b: string): number {
  return compareText(normalizeName(a), normalizeName(b)) || compareText(a, b);
}

/** `police` in `vendor-a.zip/police` — unique within a scan, so a row can be addressed. */
export function spawnCodeKey(code: SpawnCode): string {
  return `${normalizeName(code.identity)}\u0000${normalizeName(code.name)}`;
}

// ─── Naming a file across a whole scan ───────────────────────────────────────

/**
 * A pack-relative path, qualified by the resource it belongs to.
 *
 * `vehicles.meta` names a different file in every pack in a drop, and every key
 * this layer dedupes and groups findings on is that bare path: `findingKey` is
 * code + file + line + title, and `groupByEntity` keys on the entity id. So two
 * packs that each ship a malformed `vehicles.meta` produced *one* row on the
 * findings surface, the second pack's parse error never reached the page, and
 * the surviving row named neither pack. `FindingsList`'s own header calls this
 * out and leaves it to the caller to dedupe per resource; this is the caller,
 * and this is the answer.
 *
 * `SpawnCode.source` has always been spelled this way. Findings are spelled the
 * same way so a row a reader clicks and the code row above it agree about which
 * `police` is being talked about.
 */
function qualifyPath(identity: string, path: string): string {
  return `${identity}/${path}`;
}

/**
 * The entity kinds whose id is a path inside one resource.
 *
 * `vehicle` and `modkit` are deliberately absent: a spawn name is a name the
 * whole scan shares — that is what the duplicate badge is about — so grouping
 * two packs' findings about `wscop` under one heading is right, and which pack
 * each of those rows came out of is carried by the row's own qualified `file`.
 */
const FILE_SCOPED_ENTITIES: ReadonlySet<GtaEntityKind> = new Set<GtaEntityKind>([
  "resource",
  "manifest",
]);

/** One finding, re-spelled so it can be told apart from the same problem in another pack. */
function qualifyFinding(identity: string, finding: GtaFinding): GtaFinding {
  const out: GtaFinding = { ...finding };
  if (typeof finding.file === "string" && finding.file !== "") {
    out.file = qualifyPath(identity, finding.file);
  }
  if (finding.entity && FILE_SCOPED_ENTITIES.has(finding.entity.kind)) {
    out.entity = { ...finding.entity, id: qualifyPath(identity, finding.entity.id) };
  }
  return out;
}

/** Whether a qualified path names a file inside this resource. */
function belongsTo(identity: string, path: string | undefined): boolean {
  if (typeof path !== "string" || path === "") return false;
  return pathKey(path).startsWith(`${pathKey(identity)}/`);
}

// ─── Stock vehicle names ─────────────────────────────────────────────────────

/**
 * The stock spawn names, built on first use.
 *
 * `vehicleMeta.ts` keeps its own lazy copy of this set and does not export it,
 * and the duplication is one `new Set` over an array that is already in the
 * bundle — not a second copy of the table. Building it at import time is the
 * thing worth avoiding, because a public page pays for that on every load
 * whether or not anyone drops a folder on it.
 */
let cachedVanillaModels: ReadonlySet<string> | null = null;

function isVanillaVehicleName(name: string, index?: VanillaAssetNameIndex): boolean {
  if (!cachedVanillaModels) cachedVanillaModels = new Set(VANILLA_VEHICLE_MODELS);
  if (cachedVanillaModels.has(normalizeName(name))) return true;
  try {
    return index?.hasBasename(name, "yft") === true;
  } catch {
    // A caller-built index that throws is not evidence of anything; the compiled
    // table above has already answered for every stock vehicle either way.
    return false;
  }
}

// ─── Which findings belong in a spawn-code tool ──────────────────────────────

/**
 * The findings that change what a spawn code does, and no others.
 *
 * Filtered on `code`, which `types.ts` promises is permanent, so a reworded rule
 * keeps arriving here and a retired one fails a type check rather than silently
 * dropping out of the list. Everything omitted — mod kit ids, siren profiles,
 * texture dictionaries, audio — is a real problem that this tool does not claim
 * to have looked for, which is what `SPAWN_INDEX_SCOPE_NOTE` says out loud.
 */
const SPAWN_FINDING_CODES: ReadonlySet<GtaFindingCode> = new Set<GtaFindingCode>([
  "manifest.meta.not_registered",
  "vehicle.model.missing",
  "vehicle.model.invalid_chars",
  "vehicle.model.duplicate",
  "vehicle.model.vanilla_collision",
  "vehicle.model.file_missing",
  "vehicle.handling.missing",
  "vehicle.variation.orphan",
  "vehicle.xml.invalid",
  "vehicle.xml.unknown_kind",
]);

// ─── Reading one pack ────────────────────────────────────────────────────────

/** One `vehicles.meta` that was found, whether or not it could be read. */
type VehiclesMetaFile = {
  path: string;
  source: string;
  registered: boolean;
  /** The parser's complaint, when the file did not parse. `null` when it did. */
  error: string | null;
  /**
   * The file is in the resource's inventory and no text for it was supplied.
   *
   * A third state, and it exists because the first two could not tell the truth
   * about it. `collect.ts` refuses a text file over its per-file cap, so a
   * 5 MB `vehicles.meta` never reaches `input.texts` at all — and a pack whose
   * authoritative list was never opened was reported as a pack that has none:
   * "No vehicles.meta was found here, so every name is a guess." That is a
   * confident wrong answer about the one file the whole tool is built on.
   */
  unread: boolean;
  /** The inventory size, so the sentence about an unread file is checkable. */
  bytes: number;
  rows: ReturnType<typeof parseVehiclesMeta>;
};

/**
 * The manifest FiveM would read, or `null` when the folder has none.
 *
 * A folder with no manifest is not a resource the server can start, and that is
 * worth knowing on its own: it is the half-extracted download, and it is why
 * `pack-no-manifest` exists as a fixture.
 */
function manifestOf(input: GtaResourceInput): ParsedManifest | null {
  for (const candidate of MANIFEST_FILE_NAMES) {
    const source = input.texts.get(candidate);
    if (source === undefined) continue;
    return parseManifest(candidate, source);
  }
  return null;
}

/**
 * Whether the manifest's `data_file` block reaches this exact path.
 *
 * Globs are expanded through `manifestPatternToRegExp` rather than by hand,
 * because `data_file 'VEHICLE_METADATA_FILE' 'data/*.meta'` is a normal way to
 * register a folder of metas and reading it as a literal path would report every
 * pack that uses one as broken.
 */
function isRegistered(manifest: ParsedManifest, path: string): boolean {
  const key = pathKey(path);
  for (const entry of entriesOfKind(manifest, "data_file")) {
    if (pathKey(entry.value) === key) return true;
    if (isGlobPattern(entry.value) && manifestPatternToRegExp(entry.value).test(path)) return true;
  }
  return false;
}

/**
 * Why a `.meta` did not parse, in the parser's own words.
 *
 * `parseMetaXml` answers "is this readable" and returns `null` for every reason
 * at once, which is the right shape for a parser and the wrong shape for the
 * question a user is asking here. This is not a second parser: it parses nothing
 * the caller will use, it only asks the DOM what went wrong, and it runs only on
 * a file that has already failed.
 *
 * The size ceiling is checked first because a refused file has no parser message
 * to quote and reporting "unclosed tag" for a 40 MB file sends someone hunting
 * through a file that was never opened. It only fires for a caller that hands
 * over the text of a huge file — the desktop app reading a folder from disk. The
 * browser path never reaches it, because `collect.ts` refuses a text file over
 * 4 MB long before this module sees one; that case is the `unread` state on
 * `VehiclesMetaFile`, and it has its own sentence.
 *
 * The message is cut, and says that it was cut. The prior art's 200-character
 * truncation is not the defect — quietly discarding the result is — and an
 * unbounded parser message pasted into a Discord post is its own small failure.
 */
function xmlParseError(text: string): string {
  if (typeof text !== "string" || text.length === 0) {
    return "the file is empty, so there is nothing for the game to read.";
  }
  if (text.length > META_PARSE_CEILING_BYTES) {
    return (
      `the file is ${text.length.toLocaleString("en-US")} bytes, which is larger than any real ` +
      "vehicles.meta, so it was not parsed at all."
    );
  }
  let message = "";
  try {
    const doc = new DOMParser().parseFromString(
      text.replace(/^\uFEFF/, "").replace(/^\s+<\?xml/i, "<?xml"),
      "application/xml",
    );
    message = (doc?.querySelector("parsererror")?.textContent ?? "").replace(/\s+/g, " ").trim();
  } catch (error) {
    message = error instanceof Error ? error.message : String(error);
  }
  if (message === "") {
    return "the XML parser rejected it without saying why, which usually means the document has no root element.";
  }
  return message.length > MAX_PARSE_ERROR_CHARS
    ? `${message.slice(0, MAX_PARSE_ERROR_CHARS)}… (the parser's message is longer than this; open the file to see the rest)`
    : message;
}

/**
 * Whether a file was meant as a `vehicles.meta`, for a file that will not parse.
 *
 * `metaKindOf` is the real answer and needs a parsed document, which is exactly
 * what a broken file does not give. The name is all that is left, and it is what
 * the author's intent is written in: a file called `vehicles.meta` that does not
 * parse is the case this whole branch exists for, and a `settings.xml` that does
 * not parse is none of this tool's business.
 */
function looksLikeVehiclesMeta(path: string): boolean {
  const extension = extensionOf(path);
  if (extension !== "meta" && extension !== "xml") return false;
  return baseName(pathKey(path)).includes("vehicles");
}

/**
 * Every `vehicles.meta` in a resource: the ones that parsed, the ones that did
 * not, and the ones nobody opened.
 *
 * The third pass over `input.files` is the one worth explaining. `input.texts`
 * holds what the caller decoded, and a caller is entitled to decode less than
 * everything — `collect.ts` refuses a text file over 4 MB, which is a size real
 * DLC-scale `vehicles.meta` files reach. Reading `metas.length === 0` as "there
 * is no vehicles.meta" turned that refusal into an assertion about the pack, so
 * a file that is plainly in the inventory and plainly absent from the texts is
 * now its own answer rather than being counted as an absence.
 */
function vehiclesMetaFiles(
  input: GtaResourceInput,
  manifest: ParsedManifest | null,
): VehiclesMetaFile[] {
  const files: VehiclesMetaFile[] = [];
  const registeredAt = (path: string): boolean =>
    manifest === null ? false : isRegistered(manifest, path);

  for (const [path, source] of input.texts) {
    const extension = extensionOf(path);
    if (extension !== "meta" && extension !== "xml") continue;

    const doc = parseMetaXml(source);
    if (doc && metaKindOf(doc) === "vehicles") {
      files.push({
        path,
        source,
        registered: registeredAt(path),
        error: null,
        unread: false,
        bytes: source.length,
        rows: parseVehiclesMeta(doc),
      });
      continue;
    }
    // A document that parsed and is a `handling.meta` is somebody else's file.
    // Only an unreadable one that was *named* as a vehicles.meta lands here.
    if (doc || !looksLikeVehiclesMeta(path)) continue;
    files.push({
      path,
      source,
      registered: registeredAt(path),
      error: xmlParseError(source),
      unread: false,
      bytes: source.length,
      rows: [],
    });
  }

  for (const entry of input.files ?? []) {
    if (!entry || typeof entry.path !== "string") continue;
    if (!looksLikeVehiclesMeta(entry.path)) continue;
    if (input.texts.has(pathKey(entry.path))) continue;
    files.push({
      path: entry.path,
      source: "",
      registered: registeredAt(entry.path),
      error: null,
      unread: true,
      bytes: entry.size ?? 0,
      rows: [],
    });
  }

  return files.sort((a, b) => compareText(a.path, b.path));
}

/**
 * The display names this pack registers, by the key the game looks up.
 *
 * `gameName` and `vehicleMakeName` are label *keys*, not text: `20ambocrew` and
 * `SPACE` are what the files hold and "2020 F450 Ambulance" and "SPACE" are what
 * a player sees, and the join between them is an `AddTextEntry` in a Lua file
 * somewhere in the same pack. The prior art parses both fields and discards
 * them; exporting the raw key into a QBCore table instead of the name is the
 * same mistake wearing a hat, because the name is the column a shop menu shows.
 *
 * Keyed by `textEntryHash`, which accepts both forms an author can write — a
 * literal `0x488AD9F6` and a plain string the game hashes — because real packs
 * use both and reading only one would report half of them as unregistered.
 *
 * The two caps used to be silent, and a silent cap here is not a smaller list —
 * it is a *false sentence*. A pack with sixty locale files and a
 * `vehicle_names.lua` that registers every label it needs came back with every
 * name unresolved, and the QBCore export then stated, of the user's own pack,
 * that it "never registers" a label the pack registers on line one. So two
 * things changed: the likely files are read first, and hitting either cap is
 * reported, both in the pack's `skipped` and in `complete`, which is what stops
 * the exports from making a claim about files nobody opened.
 */
type LabelScan = {
  labels: Map<number, string>;
  /** Every Lua file in the resource was read and every entry in them kept. */
  complete: boolean;
  /** Lua files in the resource, and how many of them were actually scanned. */
  luaFiles: number;
  scanned: number;
};

/**
 * Lua files most likely to hold an `AddTextEntry`, first.
 *
 * A vehicle pack's labels live in a file whose name says so — `vehicle_names`,
 * `labels`, `text` — and its other forty Lua files are a framework's. Ordering
 * by that costs one pass and makes the cap bite the files that were never going
 * to answer, which is a better trade than raising it.
 */
const LABEL_FILE_HINTS = ["name", "label", "text"];

function likelyLabelFile(path: string): boolean {
  const name = pathKey(baseName(path));
  return LABEL_FILE_HINTS.some((hint) => name.includes(hint));
}

function registeredLabels(input: GtaResourceInput): LabelScan {
  const labels = new Map<number, string>();
  const lua = [...input.texts].filter(([path]) => extensionOf(path) === "lua");
  const ordered = [
    ...lua.filter(([path]) => likelyLabelFile(path)),
    ...lua.filter(([path]) => !likelyLabelFile(path)),
  ];

  let scanned = 0;
  let capped = false;
  for (const [, source] of ordered) {
    if (scanned >= MAX_LABEL_FILES) {
      capped = true;
      break;
    }
    scanned += 1;
    for (const entry of parseTextEntries(source)) {
      labels.set(textEntryHash(entry.key), entry.text);
      if (labels.size >= MAX_LABELS) {
        capped = true;
        return { labels, complete: false, luaFiles: lua.length, scanned };
      }
    }
  }
  return { labels, complete: !capped, luaFiles: lua.length, scanned };
}

/** What a pack registers a label key as, or `null` when it registers nothing for it. */
function resolveLabel(labels: ReadonlyMap<number, string>, key: string | null): string | null {
  if (key === null || key.trim() === "") return null;
  return labels.get(textEntryHash(key)) ?? null;
}

// ─── Building the index ──────────────────────────────────────────────────────

/**
 * Read every resource handed in and say what spawns from each.
 *
 * Takes the whole list rather than one resource at a time for the reason
 * `disambiguateLabels` does: whether two packs collide, and whether a name needs
 * qualifying, are properties of the set. A per-resource entry point could answer
 * neither, and the duplicate-across-packs badge is the single most valuable
 * thing this tool produces.
 *
 * Every resource comes back, including the ones that produced nothing. That is
 * not politeness — a pack with zero codes and a sentence saying why is the
 * answer to the question the user opened the tool with, and dropping it is how
 * the prior art turns "this pack ships no vehicles" into "this pack does not
 * exist".
 */
export function buildSpawnIndex(
  resources: readonly GtaResourceInput[],
  options: SpawnIndexOptions = {},
): SpawnIndex {
  const all = (resources ?? []).filter((input): input is GtaResourceInput => Boolean(input));
  const skipped: string[] = [];

  const kept = all.slice(0, MAX_PACKS);
  if (all.length > kept.length) {
    skipped.push(
      `Only the first ${MAX_PACKS.toLocaleString("en-US")} of ${all.length.toLocaleString("en-US")} ` +
        "resources were read, so the rest are missing from this index rather than empty. Split " +
        "the folder and scan it in parts.",
    );
  }

  // Decided once, over the whole set, and handed to every sentence below — the
  // rule in `labels.ts`. Two styles because a heading and a sentence are two
  // kinds of writing; one *decision*, so a finding that names two packs and the
  // sections a reader scrolls to cannot disagree about which `police` is which.
  const headings = disambiguateLabels(
    kept.map((input) => ({ name: input.name, identity: input.identity, origin: input.origin })),
    EM_DASH_LABEL_STYLE,
  );

  if (!options.vanillaNames) {
    skipped.push(
      "No base-game asset name index was supplied, so a code was only checked against the " +
        "base game's own vehicle spawn names. A custom name that collides with some other stock " +
        "asset is not badged here — it was not looked for.",
    );
  }

  const packs = kept.map((input, index) =>
    readPack(input, headings[index] ?? input.name, options),
  );

  return finalize(packs, skipped);
}

/** One resource, read end to end. */
function readPack(input: GtaResourceInput, label: string, options: SpawnIndexOptions): SpawnPack {
  const identity = input.identity ?? input.name;
  const origin = input.origin ?? null;
  const findings: GtaFinding[] = [];
  const skipped: string[] = [];

  const manifest = manifestOf(input);
  const metas = vehiclesMetaFiles(input, manifest);
  const labelScan = registeredLabels(input);
  const labels = labelScan.labels;

  if (!labelScan.complete) {
    skipped.push(
      `Only ${labelScan.scanned.toLocaleString("en-US")} of this resource's ` +
        `${labelScan.luaFiles.toLocaleString("en-US")} Lua files were scanned for AddTextEntry, ` +
        "so a display name or a make registered in one of the others is missing from the rows " +
        "below rather than absent from the pack.",
    );
  }

  // ── The cross-checks, run once by the module that owns them ───────────────

  const streamNames = new Set(
    input.files
      .filter((file) => file && typeof file.path === "string" && isInStreamFolder(file.path))
      .map((file) => normalizeName(baseName(file.path))),
  );
  const analysis = analyzeVehicleMeta({
    texts: input.texts,
    streamNames,
    vanillaNames: options.vanillaNames,
  });
  skipped.push(...analysis.skipped);

  /** Which cross-check failed for which model, by the entity the analyzer filed it under. */
  const failures = new Map<string, Set<GtaFindingCode>>();
  for (const vehicle of analysis.vehicles) {
    const codes = new Set<GtaFindingCode>();
    for (const finding of vehicle.findings) codes.add(finding.code);
    failures.set(normalizeName(vehicle.model), codes);
    findings.push(...vehicle.findings.filter((finding) => SPAWN_FINDING_CODES.has(finding.code)));
  }
  findings.push(...analysis.findings.filter((finding) => SPAWN_FINDING_CODES.has(finding.code)));

  // ── Registration, and the parse errors ────────────────────────────────────

  for (const meta of metas) {
    if (meta.unread) {
      // No finding: nothing in this file was read, so there is nothing to be
      // wrong about. What there is, is a caveat — and it has to be loud enough
      // that the pack's note below cannot be read as "this pack has no metadata".
      skipped.push(
        `${meta.path} is in this resource and no text for it was supplied to this reader — it is ` +
          `${meta.bytes.toLocaleString("en-US")} bytes, and the collector skips a text file over ` +
          "its size cap — so the spawn codes it declares were not read. They are missing from " +
          "this list rather than absent from the pack.",
      );
      continue;
    }
    if (meta.error !== null) {
      // Put first so `dedupeFindings` keeps it: `analyzeVehicleMeta` raises the
      // same code, file and title for this file and its detail cannot name the
      // parser's complaint, because it never asked for one. Same key, richer
      // sentence, one row.
      findings.unshift({
        code: "vehicle.xml.invalid",
        severity: "error",
        title: `${meta.path} is not readable as XML`,
        detail:
          `The XML parser stopped on it: ${meta.error} The game's meta loader stops at the first ` +
          "malformed tag in exactly the same way, so nothing in this file is applied and none of " +
          "the spawn codes it declares exist at runtime. Every code listed for this pack is a " +
          "guess read from a file name instead — fix the file and scan again to get the real list.",
        file: meta.path,
        entity: { kind: "resource", id: meta.path },
      });
      continue;
    }
    if (manifest === null || meta.registered) continue;

    const kind = dataFileKindFor(baseName(meta.path)) ?? "VEHICLE_METADATA_FILE";
    findings.push({
      code: "manifest.meta.not_registered",
      severity: "error",
      title: `${meta.path} is never loaded`,
      detail:
        `Nothing in ${manifest.file} registers it, so FiveM never reads it and not one of the ` +
        `${meta.rows.length} spawn code${meta.rows.length === 1 ? "" : "s"} below exists on a ` +
        "running server — they are listed because they are what the pack means to ship and the " +
        `repair is one line. Add: data_file '${kind}' '${meta.path}'`,
      file: manifest.file,
      entity: { kind: "manifest", id: manifest.file },
      fix: { label: `Add data_file '${kind}' '${meta.path}'`, confidence: "high" },
    });
  }

  // A pack shipped as `dlc.rpf` is the case the plan singles out as a non-goal,
  // and a non-goal is only honest if the tool says so where the answer would
  // otherwise be a confident zero. Both halves are here: the caveat, and — in
  // `packNote` — the sentence that must not read "this resource ships no
  // vehicles at all" when the vehicles are inside a file nothing can open.
  const sealedArchives = (input.files ?? [])
    .filter((entry) => entry && typeof entry.path === "string")
    .map((entry) => entry.path)
    .filter((path) => extensionOf(path) === SEALED_ARCHIVE_EXTENSION);

  if (sealedArchives.length > 0) {
    skipped.push(
      `${plural(sealedArchives.length, "file")} here ${sealedArchives.length === 1 ? "is" : "are"} ` +
        `a .${SEALED_ARCHIVE_EXTENSION} archive (${sealedArchives.slice(0, 3).join(", ")}), which ` +
        "no browser can open. Anything packed inside it — vehicles included — is invisible to " +
        "this scan rather than absent from the pack.",
    );
  }

  if (manifest === null) {
    skipped.push(
      "This folder has no fxmanifest.lua, so nothing here is registered with the server and " +
        "FiveM would not start it as a resource at all. Any spawn code below is what the files " +
        "imply, not something the game is currently loading.",
    );
  }

  // ── Tier one: what the metas declare ──────────────────────────────────────

  const codes: SpawnCode[] = [];
  const seen = new Set<string>();
  let rowsWithNoName = 0;

  for (const meta of metas) {
    for (const row of meta.rows) {
      if (row.model === "") {
        // Reported by `analyzeVehicleMeta` as `vehicle.model.missing`; counted
        // here so the pack's note can explain a total that does not add up.
        rowsWithNoName += 1;
        continue;
      }
      if (seen.has(row.model)) continue;
      if (codes.length >= MAX_CODES_PER_PACK) break;
      seen.add(row.model);

      const failed = failures.get(row.model) ?? new Set<GtaFindingCode>();
      codes.push({
        name: row.model,
        displayed: row.modelRaw,
        tier: "meta",
        confidence: "high",
        reason: meta.registered
          ? `${meta.path} declares it, and ${manifest?.file ?? "the manifest"} registers that file, ` +
            "which is exactly where the game reads a spawn name from."
          : `${meta.path} declares it. Nothing registers that file, so the game does not read it ` +
            "as things stand — the name is what the pack intends rather than what it currently does.",
        source: `${identity}/${meta.path}`,
        resource: input.name,
        resourceLabel: label,
        identity,
        origin,
        gameName: row.gameName,
        displayName: resolveLabel(labels, row.gameName),
        make: row.vehicleMakeName,
        makeName: resolveLabel(labels, row.vehicleMakeName),
        labelScanComplete: labelScan.complete,
        handlingId: row.handlingId,
        txdName: row.txdName,
        audioNameHash: row.audioNameHash,
        layout: row.layout,
        flags: row.flags,
        badges: badgesFor(row.model, {
          vanilla: isVanillaVehicleName(row.model, options.vanillaNames),
          noModel: failed.has("vehicle.model.file_missing"),
          noHandling: failed.has("vehicle.handling.missing"),
          noVariation: failed.has("vehicle.variation.orphan"),
          unregistered: !meta.registered && manifest !== null,
        }),
      });
    }
  }

  if (codes.length >= MAX_CODES_PER_PACK) {
    skipped.push(
      `${label}: only the first ${MAX_CODES_PER_PACK.toLocaleString("en-US")} spawn codes were ` +
        "listed. The rest are missing from this index rather than absent from the pack.",
    );
  }

  // Snapshotted *before* the candidate loop below, and this line is load-bearing.
  // The question each candidate has to answer is "did a vehicles.meta already
  // give this pack its list", and `codes` is the array that loop appends to — so
  // reading `codes.length` inside it answers a different question, one that flips
  // to yes the moment the first guess is pushed. That listed one vehicle out of
  // thirty on the stream-only pack this tier exists for, and demoted the rest
  // with a sentence about a vehicles.meta the pack does not have.
  const declaredByMeta = codes.length;

  // ── Tier two: what the model files suggest ────────────────────────────────

  const assets = collectStreamAssets([
    { name: input.name, identity, origin: origin ?? undefined, files: input.files },
  ]);
  const candidates = spawnCandidates(assets);

  const filtered: SpawnCode[] = [];
  for (const candidate of candidates) {
    const known = seen.has(candidate.name);
    const guessed: SpawnCode = {
      name: candidate.name,
      displayed: candidate.name,
      tier: "guess",
      confidence: candidate.confidence,
      reason: candidate.reason,
      source: candidate.source,
      resource: input.name,
      resourceLabel: label,
      identity,
      origin,
      gameName: null,
      displayName: null,
      make: null,
      makeName: null,
      // A guessed row carries no label key at all, so nothing below it can be
      // asserted about a label — but the flag travels with the row anyway, so a
      // restored guess and a meta row in the same pack cannot disagree.
      labelScanComplete: labelScan.complete,
      handlingId: null,
      txdName: null,
      audioNameHash: null,
      layout: null,
      flags: [],
      badges: badgesFor(candidate.name, {
        vanilla: isVanillaVehicleName(candidate.name, options.vanillaNames),
        noModel: false,
        noHandling: false,
        noVariation: false,
        unregistered: false,
      }),
    };

    if (known) continue;

    // A pack whose metas declare something gets its list from the metas, and the
    // models those metas do not mention are the interesting leftovers: mod shop
    // parts, usually, and occasionally a vehicle the author forgot to declare.
    // Either way they are shown rather than dropped, which is the whole rule.
    if (declaredByMeta > 0) {
      if (filtered.length < MAX_FILTERED_PER_PACK) {
        filtered.push({
          ...guessed,
          confidence: "low",
          reason:
            `No vehicles.meta here declares ${candidate.name}, so it is not a spawn code as the ` +
            `pack stands. ${candidate.reason}`,
        });
      }
      continue;
    }

    if (candidate.confidence === "low") {
      if (filtered.length < MAX_FILTERED_PER_PACK) filtered.push(guessed);
      continue;
    }
    if (codes.length < MAX_CODES_PER_PACK) codes.push(guessed);
  }

  if (candidates.length > 0 && filtered.length >= MAX_FILTERED_PER_PACK) {
    skipped.push(
      `${label}: the list of names that were seen and not listed as spawn codes stops at ` +
        `${MAX_FILTERED_PER_PACK.toLocaleString("en-US")} entries.`,
    );
  }

  const tier: SpawnTier = codes.length === 0 ? "none" : codes[0].tier === "meta" ? "meta" : "guess";
  const note = packNote({
    tier,
    codes,
    filtered,
    metas,
    hasStreamFolder: streamNames.size > 0,
    rowsWithNoName,
    sealedArchives,
  });

  return {
    name: input.name,
    label,
    identity,
    origin,
    tier,
    note: [note.headline, ...note.caveats].join(" "),
    noteHeadline: note.headline,
    noteCaveats: note.caveats,
    codes,
    filtered,
    // Deduped here, per pack, and qualified here, per pack. Both halves matter:
    // the dedupe has to run *inside* one resource so that the analyzer's thin
    // `vehicle.xml.invalid` and this module's richer one still collapse to one
    // row, and the qualification has to run *after* it so that two packs with
    // the same defect survive as two rows once `spawnIndexFindings` merges them.
    findings: sortFindings(dedupeFindings(findings)).map((finding) =>
      qualifyFinding(identity, finding),
    ),
    skipped: [...new Set(skipped)],
  };
}

/** The three facts a badge can be made of, plus the name itself. */
function badgesFor(
  name: string,
  state: {
    vanilla: boolean;
    noModel: boolean;
    noHandling: boolean;
    noVariation: boolean;
    unregistered: boolean;
  },
): SpawnBadge[] {
  const badges: SpawnBadge[] = [];
  // `duplicate` is deliberately absent here: it is a property of the set and is
  // added by `finalize`, which is the only place that can see two packs at once.
  if (state.unregistered) badges.push("unregistered");
  if (state.noModel) badges.push("no-model");
  if (state.noHandling) badges.push("no-handling");
  if (state.noVariation) badges.push("no-variation");
  if (state.vanilla) badges.push("vanilla");
  if (isEmergencyVehicleName(name).emergency) badges.push("emergency");
  return badges;
}

/**
 * The sentence under a pack's heading, and the reason a zero is a zero.
 *
 * There is always one. "Resource X: 0 codes, and here is why" is the answer the
 * prior art replaces with an absence, and a pack that produced nothing is the
 * pack the user is most likely to be asking about.
 *
 * Returned in two parts rather than as one string, and the split is not
 * cosmetic. The headline states the tier, the count and the source — which a
 * surface with a pack heading has already drawn as a chip and a number, so it
 * is the same fact three times on one screen, once per pack, on a tool whose
 * normal input is a forty-resource folder. The caveats are the opposite kind of
 * sentence: each one narrows what the list means, so a surface may drop the
 * headline and may never drop a caveat. Callers with no heading (every export)
 * print the joined `note`.
 */
function packNote(state: {
  tier: SpawnTier;
  codes: readonly SpawnCode[];
  filtered: readonly SpawnCode[];
  metas: readonly VehiclesMetaFile[];
  hasStreamFolder: boolean;
  rowsWithNoName: number;
  sealedArchives: readonly string[];
}): { headline: string; caveats: readonly string[] } {
  const { tier, codes, filtered, metas, sealedArchives } = state;
  const broken = metas.filter((meta) => meta.error !== null);
  const unread = metas.filter((meta) => meta.unread);
  const readable = metas.filter((meta) => meta.error === null && !meta.unread);
  const unregistered = readable.filter((meta) => !meta.registered);
  const parts: string[] = [];

  // Said in every branch, because "a vehicles.meta is here and nobody opened it"
  // changes the meaning of every sentence below it. Without it the guess branch
  // states there is no vehicles.meta, which is the tool asserting the opposite
  // of what the file list in front of it says.
  const unreadNote =
    unread.length === 0
      ? ""
      : `${plural(unread.length, "vehicles.meta file is", "vehicles.meta files are")} present ` +
        "here and were not read at all, so whatever they declare is missing from this list " +
        "rather than absent from the pack.";

  if (tier === "meta") {
    parts.push(
      `${plural(codes.length, "spawn code")} read from ` +
        `${plural(readable.length, "vehicles.meta file")}, which is where the game ` +
        "reads them from.",
    );
    if (unregistered.length > 0) {
      parts.push(
        `${plural(unregistered.length, "of those files is", "of those files are")} not registered ` +
          "in the manifest, so the codes they hold are what the pack means rather than what it does.",
      );
    }
    if (unreadNote !== "") parts.push(unreadNote);
  } else if (tier === "guess") {
    parts.push(
      broken.length > 0
        ? `Every name here is a guess from a model file name, because this pack's ` +
          `${plural(broken.length, "vehicles.meta file")} could not be parsed. That is not the ` +
          "same as a pack with no metadata: a code whose model file is named differently from " +
          "its modelName is invisible to a guess, so treat this list as incomplete until the " +
          "file parses."
        : unread.length > 0
          ? "Every name here is a guess read from the model files in stream/, and it is a guess " +
            "for a pack that has a vehicles.meta rather than a pack that has none: the file was " +
            "never opened, so the authoritative list is still in it."
          : "No vehicles.meta was found here, so every name is a guess read from the model files " +
            "in stream/. A guess can miss a vehicle whose model file is named differently from " +
            "its spawn code, and can offer a mod shop part as a vehicle.",
    );
    if (broken.length > 0 && unreadNote !== "") parts.push(unreadNote);
  } else {
    parts.push("0 spawn codes.");
    if (broken.length > 0) {
      parts.push(
        `This pack ships ${plural(broken.length, "vehicles.meta file")} that could not be parsed, ` +
          "and nothing in stream/ looked like a vehicle either — so the count is a failure to " +
          "read rather than an empty pack.",
      );
    } else if (unread.length > 0) {
      parts.push(unreadNote);
    } else if (sealedArchives.length > 0) {
      parts.push(
        `This resource ships ${plural(sealedArchives.length, `.${SEALED_ARCHIVE_EXTENSION} archive`)} ` +
          `(${sealedArchives.slice(0, 3).join(", ")}), which cannot be opened in a browser, so ` +
          "its vehicles are invisible here rather than absent. There is nothing else to read: no " +
          "vehicles.meta and no stream/ folder outside the archive.",
      );
    } else if (metas.length === 0 && !state.hasStreamFolder) {
      parts.push(
        "There is no vehicles.meta and no stream/ folder here, so this resource ships no " +
          "vehicles at all — it is a script, a map or a shared dependency.",
      );
    } else if (metas.length === 0) {
      parts.push(
        "There is no vehicles.meta, and nothing under stream/ is a .yft model, so there is no " +
          "spawn name to read or guess.",
      );
    } else {
      parts.push(
        `The ${plural(readable.length, "vehicles.meta file")} here declared no vehicle with a ` +
          "modelName.",
      );
    }
    if (filtered.length > 0) {
      parts.push(
        `${plural(filtered.length, "name was", "names were")} seen and not listed; they are in ` +
          "the filtered list with the reason for each, and any of them can be restored.",
      );
    }
  }

  if (state.rowsWithNoName > 0) {
    parts.push(
      `${plural(state.rowsWithNoName, "entry has", "entries have")} no modelName at all and ` +
        "cannot be spawned, so they are counted nowhere in this list.",
    );
  }
  if (tier !== "none" && filtered.length > 0) {
    parts.push(
      `${plural(filtered.length, "other name was", "other names were")} seen here and not listed — ` +
        "each with the reason, and each restorable.",
    );
  }
  return { headline: parts[0], caveats: parts.slice(1) };
}

// ─── Across the packs ────────────────────────────────────────────────────────

/**
 * The duplicate badge, the flat list, and the counts.
 *
 * Separated from `readPack` because it is the step that has to run again after
 * an undo: restoring a filtered name can create the very collision this
 * computes, and an index whose rows moved but whose badges did not is the kind
 * of quiet disagreement this layer is written against.
 */
function finalize(packs: SpawnPack[], skipped: string[]): SpawnIndex {
  // The sentence spelling, decided over the same set and by the same rule as the
  // headings, so a finding that names two packs and the section a reader scrolls
  // to afterwards cannot disagree about which `police` is which. Prose here and
  // em dashes there is a difference of style, never of decision — that is what
  // `LabelStyle` is for.
  const prose = new Map<string, string>();
  disambiguateLabels(
    packs.map((pack) => ({
      name: pack.name,
      identity: pack.identity,
      origin: pack.origin ?? undefined,
    })),
    PROSE_LABEL_STYLE,
  ).forEach((label, index) => {
    prose.set(normalizeName(packs[index].identity), label);
  });

  const byName = new Map<string, SpawnCode[]>();
  for (const pack of packs) {
    for (const code of pack.codes) {
      const key = normalizeName(code.name);
      byName.set(key, [...(byName.get(key) ?? []), code]);
    }
  }

  const duplicated = new Set<string>();
  const findings: GtaFinding[] = [];
  for (const [key, rows] of byName) {
    const identities = new Set(rows.map((row) => normalizeName(row.identity)));
    if (identities.size < 2) continue;
    duplicated.add(key);

    const where = rows.map(
      (row) => prose.get(normalizeName(row.identity)) ?? row.resourceLabel,
    );
    const tags: GtaFindingTag[] = isEmergencyVehicleName(key).emergency
      ? ["emergency-impact"]
      : [];
    findings.push({
      code: "vehicle.model.duplicate",
      severity: "error",
      title: `${rows[0].displayed} is a spawn code in ${identities.size} resources`,
      detail:
        `${[...new Set(where)].join(" and ")} each declare ${key}. The game keeps one model per ` +
        "name, so whichever resource loads second is discarded and half of what the server " +
        "paid for never appears — and which half wins depends on load order, so it can change " +
        "when an unrelated resource is added. Rename the code in one of them, in vehicles.meta " +
        "and on the stream files together.",
      entity: { kind: "vehicle", id: key },
      ...(tags.length > 0 ? { tags } : {}),
    });
  }

  const withBadges = packs.map((pack) => ({
    ...pack,
    codes: pack.codes
      .map((code) =>
        duplicated.has(normalizeName(code.name)) && !code.badges.includes("duplicate")
          ? { ...code, badges: ["duplicate" as const, ...code.badges] }
          : code,
      )
      .sort((a, b) => compareNames(a.name, b.name)),
    filtered: [...pack.filtered].sort((a, b) => compareNames(a.name, b.name)),
  }));

  const codes = withBadges
    .flatMap((pack) => pack.codes)
    .sort(
      (a, b) =>
        compareNames(a.name, b.name) ||
        compareText(a.resourceLabel, b.resourceLabel) ||
        compareText(a.identity, b.identity),
    );

  return {
    packs: withBadges,
    codes,
    findings: sortFindings(dedupeFindings(findings)),
    skipped: [...new Set(skipped)],
    counts: {
      packs: withBadges.length,
      codes: codes.length,
      fromMeta: codes.filter((code) => code.tier === "meta").length,
      guessed: codes.filter((code) => code.tier === "guess").length,
      filtered: withBadges.reduce((total, pack) => total + pack.filtered.length, 0),
      empty: withBadges.filter((pack) => pack.codes.length === 0).length,
      duplicated: duplicated.size,
    },
  };
}

/**
 * Put names the ranking demoted back into the list.
 *
 * This is the undo half of "show what was filtered", and it is here rather than
 * in a view because restoring a row can change the answer: a name restored in
 * one pack may be the second half of a duplicate in another, and the badge, the
 * finding and the counts all have to follow. Rebuilding through `finalize` is
 * what guarantees they do.
 *
 * Keys are `spawnCodeKey` values. Anything unrecognised is ignored rather than
 * throwing — a stale key from a previous scan is an ordinary thing for a UI to
 * hold, and losing the whole index over one is not a trade worth making.
 */
export function restoreFiltered(index: SpawnIndex, keys: readonly string[]): SpawnIndex {
  const wanted = new Set(keys ?? []);
  if (wanted.size === 0) return index;

  const packs = index.packs.map((pack) => {
    const restored = pack.filtered.filter((code) => wanted.has(spawnCodeKey(code)));
    if (restored.length === 0) return pack;
    const remaining = pack.filtered.filter((code) => !wanted.has(spawnCodeKey(code)));
    const codes = [...pack.codes, ...restored];
    // A caveat and not a headline: it narrows what the rows mean, and it is the
    // reason a surface that drops the headline still cannot go quiet on a pack
    // whose list the user has overruled.
    const restoredCaveat =
      `${plural(restored.length, "name was", "names were")} restored by hand from ` +
      "the filtered list, so the ranking's reason for demoting it still stands and you have " +
      "overruled it.";
    const noteCaveats = [...pack.noteCaveats, restoredCaveat];
    return {
      ...pack,
      // A pack that had no codes now has some, and they are guesses the user
      // overruled the ranking on. Saying it kept a tier of `none` would be the
      // report disagreeing with its own rows.
      tier: (pack.tier === "none" ? "guess" : pack.tier) as SpawnTier,
      note: [pack.noteHeadline, ...noteCaveats].join(" "),
      noteCaveats,
      codes,
      filtered: remaining,
    };
  });

  return finalize(packs, index.skipped);
}

// ─── Exports ─────────────────────────────────────────────────────────────────

export type SpawnExportOptions = {
  /** The moment to stamp. A parameter and not a clock reading, so a test can assert on it. */
  generatedAt?: Date;
};

/** The day an export was made, as a calendar date rather than one locale's idea of one. */
function stampDate(at?: Date): string {
  const when = at instanceof Date && Number.isFinite(at.getTime()) ? at : new Date();
  return when.toISOString().slice(0, 10);
}

/**
 * The one sentence every export carries: what made it, from what, and when.
 *
 * Same shape as `gtaReportSourceStamp` and `resmonSourceStamp`, on purpose. Two
 * exports out of the same hub that footnote themselves differently read as two
 * tools by two authors, and the reader has to learn both.
 */
export function spawnIndexSourceStamp(index: SpawnIndex, options: SpawnExportOptions = {}): string {
  const counts = index?.counts;
  const read = counts
    ? `${plural(counts.packs, "resource")}, ${plural(counts.codes, "spawn code")} ` +
      `(${counts.fromMeta} from vehicles.meta, ${counts.guessed} guessed from file names)`
    : "nothing that could be read";
  return (
    `Generated by ${SPAWN_INDEX_TOOL} on ${stampDate(options.generatedAt)}, from ${read}. ` +
    `${SPAWN_INDEX_SCOPE_NOTE} Read in your browser; nothing was uploaded anywhere.`
  );
}

/** `1 pack` / `2 packs`, with an irregular plural when the caller supplies one. */
function plural(count: number, singular: string, many?: string): string {
  const n = Number.isFinite(count) ? count : 0;
  return `${n.toLocaleString("en-US")} ${n === 1 ? singular : (many ?? `${singular}s`)}`;
}

/** The badges as a reader sees them, in the order `badgesFor` fixed. */
const BADGE_LABELS: Record<SpawnBadge, string> = {
  duplicate: "duplicate across packs",
  vanilla: "replaces a base-game vehicle",
  emergency: "emergency fleet",
  "no-model": "no model file",
  "no-handling": "no handling entry",
  "no-variation": "no carvariations entry",
  unregistered: "meta not registered",
};

function describeBadges(code: SpawnCode): string {
  return code.badges.map((badge) => BADGE_LABELS[badge]).join(", ");
}

/**
 * Why a label key came back unresolved, at the strength the read can support.
 *
 * "This pack never registers it" is a claim about every Lua file in the pack,
 * and it is only true when every Lua file in the pack was read. When the label
 * scan hit its cap the honest sentence is narrower, and a file the user can go
 * and look at is named by the pack's own `skipped` line beside it.
 */
function unresolvedLabelReason(code: SpawnCode): string {
  return code.labelScanComplete
    ? "this pack never registers"
    : "no AddTextEntry in the Lua files that were read registers";
}

/**
 * The list as something to paste into Discord.
 *
 * Plain text, one code per line, grouped by pack with the tier said out loud —
 * because the answer to "what spawns from this pack" is worth nothing to the
 * person reading it in a channel if they cannot tell a name the pack declared
 * from a name this tool inferred from a file called `police_extra.yft`.
 *
 * Packs with no codes are printed with their reason rather than omitted, and the
 * filtered names come after the list with theirs. Both of those are the point of
 * the tool, so an export that dropped them would be the version of this tool
 * that the brief exists to replace.
 */
export function toSpawnText(index: SpawnIndex, options: SpawnExportOptions = {}): string {
  const lines: string[] = [];
  const counts = index.counts;
  lines.push(`Spawn codes — ${plural(counts.packs, "resource")}, ${plural(counts.codes, "code")}`);
  lines.push(spawnIndexSourceStamp(index, options));

  for (const pack of index.packs) {
    lines.push("");
    lines.push(`${pack.label} — ${plural(pack.codes.length, "code")} (${tierWord(pack.tier)})`);
    lines.push(`  ${pack.note}`);

    const width = Math.min(
      24,
      pack.codes.reduce((longest, code) => Math.max(longest, code.name.length), 0),
    );
    for (const code of pack.codes) {
      const badges = describeBadges(code);
      // A `gameName` no AddTextEntry registers is a lookup key a player sees raw,
      // not a display name. Printing it as one would be this export making the
      // same claim the QBCore table refuses to make two functions below.
      const trailer = [
        code.displayName ??
          (code.gameName === null
            ? ""
            : `${code.gameName} (${code.labelScanComplete ? "label not registered" : "no AddTextEntry found in the Lua files that were read"})`),
        code.makeName ?? code.make ?? "",
        code.tier === "guess" ? `guessed, ${code.confidence} confidence` : "",
        badges,
      ].filter((part) => part !== "");
      lines.push(`  ${code.name.padEnd(width)}${trailer.length > 0 ? `  — ${trailer.join(" · ")}` : ""}`);
    }

    if (pack.filtered.length > 0) {
      lines.push(`  Seen and not listed (${pack.filtered.length}):`);
      for (const code of pack.filtered) lines.push(`    ${code.name} — ${code.reason}`);
    }
    for (const text of pack.skipped) lines.push(`  Not checked: ${text}`);
  }

  if (index.skipped.length > 0) {
    lines.push("");
    lines.push("Not checked:");
    for (const text of index.skipped) lines.push(`  ${text}`);
  }
  return lines.join("\n");
}

function tierWord(tier: SpawnTier): string {
  if (tier === "meta") return "from vehicles.meta";
  return tier === "guess" ? "guessed from file names" : "nothing to read";
}

const CSV_HEADER = [
  "listed",
  "resource",
  "resource_origin",
  "resource_identity",
  "spawn_code",
  "tier",
  "confidence",
  "badges",
  "display_name",
  "game_name",
  "make_name",
  "vehicle_make_name",
  "handling_id",
  "txd_name",
  "audio_name_hash",
  "layout",
  "flags",
  "source_file",
  "how_we_know",
];

/**
 * The same list as a spreadsheet, filtered names included.
 *
 * `listed` is the first column rather than a second file, because the whole
 * argument for showing what was filtered is that it stays next to what was not:
 * a reader sorting on that column sees both halves and can act on either, and a
 * separate download is one a nobody opens.
 *
 * Every cell goes through `csv.ts`. Half of these values are strings out of a
 * stranger's `vehicles.meta`, and a `gameName` beginning `=` is untrusted input
 * arriving in a file the user opens in Excel without thinking about it.
 */
export function toSpawnCsv(index: SpawnIndex, options: SpawnExportOptions = {}): string {
  const lines = [CSV_HEADER.join(",")];

  const row = (code: SpawnCode, listed: boolean): void => {
    lines.push(
      [
        listed ? "listed" : "filtered",
        code.resource,
        code.origin ?? "",
        code.identity,
        code.name,
        code.tier,
        code.confidence,
        code.badges.join(" "),
        code.displayName ?? "",
        code.gameName ?? "",
        code.makeName ?? "",
        code.make ?? "",
        code.handlingId ?? "",
        code.txdName ?? "",
        code.audioNameHash ?? "",
        code.layout ?? "",
        code.flags.join(" "),
        code.source,
        code.reason,
      ]
        .map(csvCell)
        .join(","),
    );
  };

  for (const pack of index.packs) {
    for (const code of pack.codes) row(code, true);
    for (const code of pack.filtered) row(code, false);
  }

  // The caveats ride in the table under their own `listed` value rather than as
  // a banner above it, for the reason `report.ts` gives: a banner is what stops
  // a CSV from parsing, and a spreadsheet that quietly claims everything was
  // checked is the one copy of the report that outlives the screen.
  const note = (resource: string, identity: string, text: string): void => {
    lines.push(
      ["note", resource, "", identity, "", "", "", "", "", "", "", "", "", "", "", "", "", "", text]
        .map(csvCell)
        .join(","),
    );
  };
  for (const pack of index.packs) {
    note(pack.name, pack.identity, pack.note);
    for (const text of pack.skipped) note(pack.name, pack.identity, text);
  }
  for (const text of index.skipped) note("", "", text);
  note("", "", spawnIndexSourceStamp(index, options));

  // CRLF, like every other CSV this layer writes: it is what Excel expects from
  // a file it did not write, and every other reader accepts it.
  return lines.join("\r\n");
}

/** `'` and `\` escaped for a single-quoted Lua string, and newlines flattened. */
function luaString(value: string): string {
  return `'${value.replace(/\\/g, "\\\\").replace(/'/g, "\\'").replace(/[\r\n]+/g, " ")}'`;
}

/** A comment body that cannot escape its line and start executing. */
function luaComment(value: string): string {
  return value.replace(/[\r\n]+/g, " ");
}

/**
 * The plain Lua table: a list of spawn codes, and nothing else.
 *
 * The `-- guessed` comment is the whole reason this is not a bare list of
 * strings. The meta-versus-guess distinction is the only real insight the tool
 * has, and the prior art loses it at exactly the moment it matters — the user
 * hits Copy, pastes the list into a script, and the two tiers become one list of
 * names nobody can tell apart again. A comment on the line preserves it for free
 * and survives every paste.
 */
export function toVehiclesLua(index: SpawnIndex, options: SpawnExportOptions = {}): string {
  const lines: string[] = [
    `-- ${luaComment(spawnIndexSourceStamp(index, options))}`,
    "-- A line marked `guessed` was read from a model file name, not from vehicles.meta.",
    "",
    "local vehicles = {",
  ];

  for (const pack of index.packs) {
    lines.push(`    -- ${luaComment(pack.label)}: ${luaComment(pack.note)}`);
    for (const code of pack.codes) {
      const notes = [
        code.tier === "guess" ? `guessed, ${code.confidence} confidence` : "",
        describeBadges(code),
      ].filter((part) => part !== "");
      lines.push(
        `    ${luaString(code.name)},${notes.length > 0 ? ` -- ${luaComment(notes.join("; "))}` : ""}`,
      );
    }
  }

  lines.push("}", "", "return vehicles", "");
  return lines.join("\n");
}

/**
 * QBCore's `shared/vehicles.lua`, with the make and the display name in it.
 *
 * This is the export the tool is for. A spawn code on its own is a name to type;
 * a spawn code with its manufacturer and its display name is a row in a
 * dealership, which is what the server owner was going to spend the evening
 * typing out by hand. Both fields are already in `vehicles.meta` — the prior art
 * parses them and throws them away.
 *
 * Two honesty rules shape the output, and both are visible in the file:
 *
 * - `name` is the *registered label*, resolved through this pack's own
 *   `AddTextEntry`, and falls back to the raw `gameName` key with a comment
 *   saying so. `<gameName>20ambocrew</gameName>` is a lookup key, and a shop menu
 *   showing `20ambocrew` to a player is the failure this avoids.
 *
 * - `price`, `category` and `shop` are values no pack states. They are emitted
 *   because a QBCore table without them does not load, each one is marked as a
 *   placeholder on its own line, and the header says so first. Writing a
 *   plausible-looking `2500` and `'sedan'` would be this layer inventing data,
 *   which it does not do — a placeholder somebody has to grep for is the
 *   honest version of a field we cannot know.
 */
export function toQbSharedLua(index: SpawnIndex, options: SpawnExportOptions = {}): string {
  const lines: string[] = [
    `-- ${luaComment(spawnIndexSourceStamp(index, options))}`,
    "-- price, category and shop are PLACEHOLDERS. No vehicle pack states them, and this tool",
    "-- does not invent values — search this file for `placeholder` and set them yourself.",
    "-- A row marked `guessed` was read from a model file name, not from vehicles.meta.",
    "",
    "QBShared = QBShared or {}",
    "QBShared.Vehicles = QBShared.Vehicles or {}",
  ];

  for (const pack of index.packs) {
    lines.push("");
    lines.push(`-- ${luaComment(pack.label)}: ${luaComment(pack.note)}`);
    for (const code of pack.codes) {
      const notes = [
        code.tier === "guess" ? `guessed from ${luaComment(code.source)}` : "",
        describeBadges(code),
      ].filter((part) => part !== "");
      if (notes.length > 0) lines.push(`-- ${luaComment(notes.join("; "))}`);

      const name = code.displayName ?? code.gameName ?? code.name;
      const nameNote =
        code.displayName !== null
          ? ""
          : code.gameName !== null
            ? ` -- gameName is a text label ${unresolvedLabelReason(code)}; players see it raw`
            : " -- the pack states no display name; this is the spawn code";
      const brand = code.makeName ?? code.make ?? "";
      const brandNote =
        code.makeName !== null || code.make === null
          ? ""
          : ` -- vehicleMakeName is a text label ${unresolvedLabelReason(code)}`;

      lines.push(`QBShared.Vehicles[${luaString(code.name)}] = {`);
      lines.push(`    ['name'] = ${luaString(name)},${nameNote}`);
      lines.push(`    ['brand'] = ${luaString(brand)},${brandNote}`);
      lines.push(`    ['model'] = ${luaString(code.name)},`);
      lines.push(`    ['hash'] = \`${code.name}\`,`);
      lines.push("    ['price'] = 0, -- placeholder");
      lines.push("    ['category'] = 'unknown', -- placeholder");
      lines.push("    ['shop'] = 'pdm', -- placeholder");
      lines.push("}");
    }
  }

  lines.push("");
  return lines.join("\n");
}

/**
 * The findings a surface renders, packs and cross-pack together.
 *
 * One call rather than leaving a view to concatenate, because the order matters
 * and is the shared one: `sortFindings` puts errors first and the police car
 * above the sedan, and a view that appended its own way would rank a pack's
 * missing model file above a duplicate that takes a whole vehicle off the
 * server.
 *
 * `dedupeFindings` runs over the cross-pack findings only. A pack's own findings
 * were already deduped inside that pack and are merged as they are, because
 * `findingKey` is code + file + line + title and two packs with the same defect
 * write the same three of those — running it again across the set is what
 * collapsed ten broken packs into one row. Their `file` is resource-qualified,
 * so nothing here can merge across the identity boundary intake exists to keep.
 */
export function spawnIndexFindings(index: SpawnIndex): GtaFinding[] {
  return sortFindings([
    ...dedupeFindings(index.findings),
    ...index.packs.flatMap((pack) => pack.findings),
  ]);
}

/**
 * Every one-click fix this index can offer, with its edits filled in.
 *
 * Kept as a separate call from `spawnIndexFindings` because it needs the
 * manifest source the findings do not carry, and because a surface that only
 * wants to *show* the findings should not have to hand back the resources to do
 * it. `attachManifestFixes` is the shared filler: the finding's own `fix.label`
 * is what names the line to add, so the edit this produces is the same one
 * PackBench's manifest fix produces for the same problem.
 *
 * The matching is the part worth reading. `attachManifestFixes` was written for
 * one resource's findings and claims any unfilled fix it is handed — it checks
 * `finding.fix`, never `finding.file` — so looping it over a whole scan let the
 * first pack with a manifest fill in a repair for a *different* pack's finding.
 * The result applied cleanly, because `expect` on an append is the empty string
 * and nothing can verify an append into the wrong file, and the page then
 * offered the untouched pack's manifest as the repaired one while the broken
 * pack stayed broken. So each resource is shown only the findings whose
 * qualified `file` is inside it, and the edit that comes back is re-qualified
 * the same way, so the surface that reads the file back cannot resolve it
 * against the wrong pack either.
 */
export function attachSpawnIndexFixes(
  findings: readonly GtaFinding[],
  resources: readonly GtaResourceInput[],
): GtaFinding[] {
  let out = [...findings];
  for (const input of resources ?? []) {
    if (!input) continue;
    const identity = input.identity ?? input.name;

    let context: { manifest: ParsedManifest; source: string } | null = null;
    for (const candidate of MANIFEST_FILE_NAMES) {
      const source = input.texts.get(candidate);
      if (source === undefined) continue;
      context = { manifest: parseManifest(candidate, source), source };
      break;
    }
    if (!context) continue;

    out = out.map((finding) => {
      if (!belongsTo(identity, finding.file)) return finding;
      const [filled] = attachManifestFixes([finding], context);
      const fix = filled?.fix;
      const edits = fix?.edits;
      if (!filled || !fix || !edits || edits.length === 0) return finding;
      return {
        ...filled,
        fix: {
          ...fix,
          edits: edits.map((edit) => ({ ...edit, file: qualifyPath(identity, edit.file) })),
        },
      };
    });
  }
  return out;
}
